import { useDeviceContext } from '@/contexts/DeviceContext';
import { useManifest } from '@/contexts/ManifestContext';
import clsx from 'clsx';
import React from 'react';

{
  /*
    NOTE: classnames are merged with clsx and not cn (which uses tailwind-merge),
    since when using custom text-* classes overriding the color will override
    other properties of the class such as the font-size, etc.
  */
}

interface TypographyProps {
  children: React.ReactNode;
  className?: string;
}

export const Heading1 = ({ children, className }: TypographyProps) => {
  const { features } = useManifest();

  return (
    <h1
      className={clsx(
        'font-display text-h1 leading-h1 lg:text-h1-lg font-bold',
        features.typographyUppercase ? 'uppercase' : '',
        className,
      )}
    >
      {children}
    </h1>
  );
};

export const Heading1Small = ({ children, className }: TypographyProps) => {
  const { features } = useManifest();
  return (
    <h1
      className={clsx(
        'font-display text-section-title leading-section-title lg:text-section-title-lg font-bold',
        features.typographyUppercase ? 'uppercase' : '',
        className,
      )}
    >
      {children}
    </h1>
  );
};

export const Heading2 = ({ children, className }: TypographyProps) => {
  const { features } = useManifest();

  return (
    <h2
      className={clsx(
        'font-display text-h2 leading-h2 lg:text-h2-lg font-bold',
        features.typographyUppercase ? 'uppercase' : '',
        className,
      )}
    >
      {children}
    </h2>
  );
};

export const Heading3 = ({ children, className }: TypographyProps) => {
  const { features } = useManifest();

  return (
    <h3
      className={clsx(
        'font-display text-h3 leading-h3 lg:text-h3-lg font-bold',
        features.typographyUppercase ? 'uppercase' : '',
        className,
      )}
    >
      {children}
    </h3>
  );
};

export const Heading4 = ({ children, className }: TypographyProps) => {
  const { features } = useManifest();

  return (
    <h4
      className={clsx(
        'font-display text-h4 leading-h4 lg:text-h4-lg font-bold',
        features.typographyUppercase ? 'uppercase' : '',
        className,
      )}
    >
      {children}
    </h4>
  );
};

export const Heading5 = ({ children, className }: TypographyProps) => {
  const { features } = useManifest();

  return (
    <h5
      className={clsx(
        'font-display text-h5 leading-h5 lg:text-h5-lg font-bold',
        features.typographyUppercase ? 'uppercase' : '',
        className,
      )}
    >
      {children}
    </h5>
  );
};

export const BodyLarge = ({ children, className }: TypographyProps) => {
  return (
    <span
      className={clsx(
        'font-body text-body-lg leading-body-lg lg:text-body-lg-desktop',
        className,
      )}
    >
      {children}
    </span>
  );
};

export const BodyMedium = ({ children, className }: TypographyProps) => {
  return (
    <span className={clsx('font-body text-body-md leading-body-md', className)}>
      {children}
    </span>
  );
};

export const BodyMediumList = ({ children, className }: TypographyProps) => {
  return (
    <BodyMedium className={className}>
      <ul className="my-4 list-disc space-y-2 pl-6">{children}</ul>
    </BodyMedium>
  );
};

export const BodySmall = ({ children, className }: TypographyProps) => {
  return (
    <span
      className={clsx(
        'font-body text-body-sm leading-body-sm lg:text-body-sm-desktop lg:leading-body-sm-desktop',
        className,
      )}
    >
      {children}
    </span>
  );
};

export const MainMenu = ({ children, className }: TypographyProps) => {
  const { features } = useManifest();

  return (
    <span
      className={clsx(
        'font-display text-mainmenu font-bold',
        features.typographyUppercase ? 'uppercase' : '',
        className,
      )}
    >
      {children}
    </span>
  );
};

export const Navigation = ({ children, className }: TypographyProps) => {
  return (
    <span
      className={clsx(
        'font-display text-nav-3xs 2xs:text-nav leading-nav lg:text-nav-lg font-bold',
        className,
      )}
    >
      {children}
    </span>
  );
};

export const ViewAll = ({ children, className }: TypographyProps) => {
  const { features } = useManifest();

  return (
    <span
      className={clsx(
        'font-display text-view-big leading-view lg:text-view-big-lg font-bold',
        features.typographyUppercase ? 'uppercase' : '',
        className,
      )}
    >
      {children}
    </span>
  );
};

export const ViewAllSmall = ({ children, className }: TypographyProps) => {
  return (
    <span
      className={clsx(
        'font-display text-view leading-view lg:text-view-lg font-bold',
        className,
      )}
    >
      {children}
    </span>
  );
};

export const HowItWorksFont = ({ children, className }: TypographyProps) => {
  return (
    <span
      className={clsx(
        'font-display text-howitworks leading-howitworks font-bold',
        className,
      )}
    >
      {children}
    </span>
  );
};

export const SectionTitle = ({ children, className }: TypographyProps) => {
  const { isTouch } = useDeviceContext();
  const { features } = useManifest();

  return (
    <span
      className={clsx(
        'font-display leading-section-title font-bold',
        features.typographyUppercase ? 'uppercase' : '',
        isTouch
          ? 'text-section-title lg:text-section-title-lg'
          : 'text-section-title-lg',
        className,
      )}
    >
      {children}
    </span>
  );
};

export const CardTitle = ({ children, className }: TypographyProps) => {
  const { features } = useManifest();

  return (
    <span
      className={clsx(
        'font-display text-card-title leading-card-title lg:text-card-title-lg',
        features.typographyUppercase ? 'uppercase' : '',
        className,
      )}
    >
      {children}
    </span>
  );
};

export const CardBody = ({ children, className }: TypographyProps) => {
  return (
    <span
      className={clsx(
        'font-body text-card-body leading-card-body lg:text-card-body-lg',
        className,
      )}
    >
      {children}
    </span>
  );
};

export const CategorySm = ({ children, className }: TypographyProps) => {
  return (
    <span className={clsx('font-body text-category font-bold', className)}>
      {children}
    </span>
  );
};

export const CategoryMd = ({ children, className }: TypographyProps) => {
  return (
    <span
      className={clsx(
        'font-body text-category-md font-weight-category leading-category',
        className,
      )}
    >
      {children}
    </span>
  );
};

export const CategoryLg = ({ children, className }: TypographyProps) => {
  return (
    <span
      className={clsx(
        'font-body text-category-lg font-weight-category leading-category italic',
        className,
      )}
    >
      {children}
    </span>
  );
};
